<script setup>
import { toRaw, watch, inject } from 'vue';
import { useUserStore } from '@/stores/user';
import { useForm } from 'laravel-precognition-vue';

const form = useForm('patch', '/api/v1/account', {
    hash: null,
    email: null,
    first_name: null,
    last_name: null,
    phone_number: null,
    dob: null,
    gender: null,
    password: null,
    password_confirmation: null,
    navigation: 'side',
});

const userStore = useUserStore();

watch(() => userStore.profile, (val) => {
    if (val) {
        form.setData(toRaw({ ...val }));
    }
});

watch(() => userStore.profile?.member, (val) => {
    if (val) {
        if (val.dob) {
            const [year, month, day] = val.dob.split('-');
            form.dob = new Date(Number(year), Number(month) - 1, Number(day));
        }
        form.phone_number = val.phone_number || null;
        form.gender = val.gender || null;
    }
    
});

const displayExtraFields = () => {
    if (userStore.profile && userStore.profile.member) {
        return true;
    }
    return false;
};

const modals = inject('modals');
const { fetchUser } = userStore;

const openMemberModal = () => {
    modals.show('MembersModal', {
        props: {
            member: {
                ...userStore.profile.member,
                user: {
                    ...userStore.profile
                }
            },
            mode: 'Edit',
        }
    }).then(() => {
        fetchUser(form.data());
    });
};

const cancel = () => {
    window.history.go(-1);
};

const save = () => {
    if (form.dob) {
        form.dob = form.dob.toISOString().split('T')[0];
    }
    form.submit();
};
</script>

<template>
<MainContentLayout>
    <template #breadcrumb>
        <BreadCrumb />
    </template>
    <template #title>
        {{ $t('label.profile') }}
    </template>
    <template #actions>
        <StyledButton
            size="small"
            variant="outlined"
            @click="$inertia.visit('/account/security-questions')">
            <FontAwesomeIcon
                icon="lock"
                class="mr-1" />
            {{ $t('label.change_security_questions_and_answers') }}
        </StyledButton>
    </template>
    <form class="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full lg:w-1/2">
        <StyledInput
            v-model="form.first_name"
            :label="$t('label.first_name')"
            :placeholder="$t('label.first_name')"
            name="first_name"
            @change="form.validate('first_name')" />
        <StyledInput
            v-model="form.last_name"
            :label="$t('label.last_name')"
            :placeholder="$t('label.last_name')"
            name="last_name"
            @change="form.validate('last_name')" />
        <StyledInput
            v-model="form.email"
            :label="$t('label.email')"
            :placeholder="$t('label.email')"
            name="email"
            type="email"
            @change="form.validate('email')" />
        <StyledMask
            v-if="displayExtraFields()"
            v-model="form.phone_number"
            name="phone_number"
            mask="(###) ###-####"
            :label="$t('label.phone_number')" />
        <StyledDatepicker 
            v-if="displayExtraFields()"
            v-model="form.dob"
            :required="false"
            :past-only="true"
            name="dob"
            :label="$t('label.date_of_birth')" />
        <StyledDropdown
            v-if="displayExtraFields()"
            v-model="form.gender"
            :label="$t('label.gender')"
            :placeholder="$t('label.gender')"
            :options="[
                { label: $t('label.male'), value: 'male' },
                { label: $t('label.female'), value: 'female' },
                { label: $t('label.other'), value: 'other' }
            ]"
            name="gender" />
        <StyledInput
            v-model="form.password"
            :label="$t('label.password')"
            :placeholder="$t('label.password')"
            name="password"
            autocomplete="new-password"
            type="password" />
        <StyledInput
            v-model="form.password_confirmation"
            :label="$t('label.password_confirmation')"
            :placeholder="$t('label.password_confirmation')"
            name="password_confirmation"
            type="password" />
        <StyledButton
            v-if="displayExtraFields()"
            @click="openMemberModal">
            {{ $t('label.edit_user_details') }}
        </StyledButton>
        
        <StyledSaveButton @save="save" @cancel="cancel" />
    </form>
</MainContentLayout>
</template>
